import { useEffect, useRef, useState } from 'react';

import { Skeleton, useColorModeValue } from '@chakra-ui/react';
import { ButtonToggle } from '@peacecover/component';
import { ChevronDown } from '@peacecover/icon';
import { t } from 'i18next';
import { isEmpty } from 'lodash';

const HorizontalScrollMembers = ({
  members,
  selectedMembers = [],
  onSelectedMembers,
  showEntire = true
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const startColor = useColorModeValue('separator.light.1', 'separator.dark.1');
  const endColor = useColorModeValue('separator.light.2', 'separator.dark.2');
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (isEmpty(members)) {
    return (
      <Skeleton
        height="33px"
        width="95px"
        borderRadius={'32px'}
        startColor={startColor}
        endColor={endColor}
      />
    );
  }

  const isNoneSelected = selectedMembers.length === 0;

  const selectedLabel = isNoneSelected
    ? t('MemberDropdown.entireMember')
    : selectedMembers.length === 1
      ? selectedMembers[0].name
      : t('MemberDropdown.multipleSelected', {
          name: selectedMembers[0].name,
          count: selectedMembers.length - 1
        });

  const handleSelectAll = () => {
    onSelectedMembers([]);
    setIsOpen(false);
  };

  const handleToggleMember = (member) => {
    const isSelected = selectedMembers.some((m) => m.memberId === member.memberId);
    if (isSelected) {
      onSelectedMembers(selectedMembers.filter((m) => m.memberId !== member.memberId));
    } else {
      onSelectedMembers([...selectedMembers, member]);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-[4px] px-[12px] py-[8px] rounded-full ${isNoneSelected ? 'bg-background-elevated ring-1 ring-inset ring-separator1' : 'bg-secondary-light dark:bg-secondary-dark'}`}
      >
        <span
          className={`text-[14px] font-semibold ${isNoneSelected ? 'text-text-main-lv2' : 'text-on-secondary'}`}
        >
          {selectedLabel}
        </span>
        <ChevronDown
          size={14}
          className={`transition-transform duration-200 flex-shrink-0 ${isOpen ? 'rotate-180' : ''} ${isNoneSelected ? 'text-text-main-lv2' : 'text-on-secondary'}`}
        />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-[6px] bg-background-elevated rounded-[12px] shadow-[0_0_10px_0_rgba(0,0,0,0.04)] z-50 py-[12px] max-h-[200px] overflow-y-auto">
          {showEntire && (
            <button
              type="button"
              onClick={handleSelectAll}
              className="flex items-center gap-[6px] pl-[12px] pr-[24px] py-[8px] w-full"
            >
              <ButtonToggle type="check" size="sm" checked={isNoneSelected} />
              <span
                className={`text-[14px] font-medium leading-[17px] text-nowrap truncate ${isNoneSelected ? 'text-primary-light dark:text-primary-dark' : 'text-text-main-lv4'}`}
              >
                {t('MemberDropdown.entireMember')}
              </span>
            </button>
          )}
          {members.map((member) => {
            const isSelected = selectedMembers.some((m) => m.memberId === member.memberId);
            return (
              <button
                key={member.memberId}
                type="button"
                onClick={() => handleToggleMember(member)}
                className="flex items-center gap-[6px] pl-[12px] pr-[24px] py-[8px] w-full"
              >
                <ButtonToggle type="check" size="sm" checked={isSelected} />
                <span
                  className={`text-[14px] font-medium leading-[17px] text-nowrap truncate ${isSelected ? 'text-primary-light dark:text-primary-dark' : 'text-text-main-lv4'}`}
                >
                  {member.name}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default HorizontalScrollMembers;
